import React, { useState, useEffect } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { fetchAPI } from "../utils/fetchAPI";

interface SteelItem {
  id: number;
  name: string;
  steelType: number;
  steelStatus: number;
  width: number;
  height: number;
  thickness: number;
  weight: number;
  grade: string;
  usedTo: string;
  info: string;
}

interface EditSteelFormProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  item: SteelItem;
}

const EditSteelForm: React.FC<EditSteelFormProps> = ({ show, onHide, onSuccess, item }) => {
  const [formData, setFormData] = useState<SteelItem>({
    id: 0,
    name: "",
    steelType: 1,
    steelStatus: 0,
    width: 0,
    height: 0,
    thickness: 0,
    weight: 0,
    grade: "",
    usedTo: "",
    info: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (item) {
      setFormData(item);
    }
  }, [item]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");
    setIsLoading(true);

    try {
      const data = await fetchAPI("POST", "updateSteel.php", formData);

      if (data && data.success) {
        onSuccess();
        onHide();
      } else {
        throw new Error(data?.message || "Nie udało się zaktualizować pozycji stali.");
      }
    } catch (error: any) {
      setErrorMessage(error.message || "Wystąpił błąd podczas aktualizacji pozycji.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edytuj pozycję stali</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {errorMessage && <div className="text-danger mb-3">{errorMessage}</div>}

          <Form.Group className="mb-3">
            <Form.Label>Nazwa</Form.Label>
            <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} required />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Typ stali</Form.Label>
            <Form.Select name="steelType" value={formData.steelType} onChange={handleChange} required>
              <option value="1">Cały arkusz</option>
              <option value="2">Odpad użyteczny</option>
              <option value="3">Wykorzystana</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select name="steelStatus" value={formData.steelStatus} onChange={handleChange} required>
              <option value="0">Dostępna</option>
              <option value="1">W użyciu</option>
              <option value="2">Zarezerwowana</option>
              <option value="3">Zniszczona</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Szerokość</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              name="width"
              value={formData.width}
              onChange={handleChange}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Długość</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              name="height"
              value={formData.height}
              onChange={handleChange}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Grubość</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              name="thickness"
              value={formData.thickness}
              onChange={handleChange}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Waga</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              name="weight"
              value={formData.weight}
              onChange={handleChange}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Gatunek</Form.Label>
            <Form.Control type="text" name="grade" value={formData.grade} onChange={handleChange} required />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Przeznaczenie</Form.Label>
            <Form.Control type="text" name="usedTo" value={formData.usedTo} onChange={handleChange} required />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Informacje dodatkowe</Form.Label>
            <Form.Control as="textarea" name="info" value={formData.info} onChange={handleChange} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" disabled={isLoading}>
            {isLoading ? "Aktualizowanie..." : "Zapisz zmiany"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditSteelForm;
